// src/pages/PricingPage.jsx
//
// Public pricing reference page (no auth required). Lives at /pricing.
//
// Design convention: every PUBLIC page (login, register, pricing,
// marketing site) uses the pitch-dark background `#0d1f14` with
// lime accents. Authenticated app pages use the light `#f7faf5`
// background. This file MUST stay dark to match.
//
// IMPORTANT: This is intentionally an INFORMATIONAL page only. There
// are no checkout flows, no plan-selection state, no per-account
// gates. Per pricing-strategy-v2.md, in-app pricing UI doesn't ship
// until Phase 3 (after 10 founding clubs validate the tiers).

import React from 'react'
import { Link } from 'react-router-dom'
import {
  CheckIcon,
  RocketLaunchIcon,
  ChevronRightIcon,
  StarIcon,
} from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"
// Public contact alias. Forwards to the founder; keeps the personal
// inbox out of public-facing pages.
const FOUNDER_EMAIL = 'hello@getscrimly.com'

// Pre-fills both subject and body so we can sort the inbox at a
// glance and the prospect doesn't have to figure out what to write.
function mailto(subject, tier) {
  const body = tier
    ? `Hi Scrimly team,\n\nI'd like to know more about the ${tier} plan.\n\nA bit about us:\n  - Club / team name:\n  - Number of teams:\n  - Roughly how many players:\n  - Currently using:\n\nThanks!`
    : ''
  return `mailto:${FOUNDER_EMAIL}?subject=${encodeURIComponent(subject)}${body ? `&body=${encodeURIComponent(body)}` : ''}`
}

// ----------------------------------------------------------------
// Tier data, mirrors docs/pricing-strategy-v2.md
// ----------------------------------------------------------------
const TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    annualNote: null,
    blurb: 'Solo rec coach',
    teams: '1 team',
    players: '25 players',
    features: [
      'Roster, schedule, RSVPs',
      'Team chat & announcements',
      'Mobile app for parents & players',
      'Email & push notifications',
    ],
    notIncluded: [
      'Online payments',
      'SMS broadcasts',
      'Multi-team / club',
    ],
    ctaText: 'Sign up free',
    ctaHref: '/register',
    ctaIsRoute: true,
    highlight: false,
  },
  {
    id: 'team',
    name: 'Team',
    price: '$29',
    period: '/mo',
    annualNote: '$24/mo billed annually',
    blurb: 'Single competitive team with dues',
    teams: '1 team',
    players: 'Unlimited players',
    features: [
      'Everything in Free',
      'Online registration & payments',
      'Stripe pass-through (2.9% + $0.30)',
      'Coach <> parent direct messages',
      'Email support',
    ],
    ctaText: 'Get in touch',
    ctaHref: mailto('Team tier inquiry', 'Team ($29/mo)'),
    highlight: false,
  },
  {
    id: 'club',
    name: 'Club',
    price: '$99',
    period: '/mo',
    annualNote: '$82/mo billed annually',
    blurb: 'Small academy, 2-3 teams',
    teams: '3 teams',
    players: '75 players',
    features: [
      'Everything in Team',
      'Multi-team admin & dashboard',
      'Basic reporting',
      'Session scheduling',
      'Bilingual SMS broadcasts',
    ],
    ctaText: 'Get in touch',
    ctaHref: mailto('Club tier inquiry', 'Club ($99/mo)'),
    highlight: true,
    badge: 'Most Popular',
  },
  {
    id: 'growth',
    name: 'Growth',
    price: '$349',
    period: '/mo',
    annualNote: '$290/mo billed annually',
    blurb: 'Competitive club',
    teams: '10 teams',
    players: '250 players',
    features: [
      'Everything in Club',
      'Player development tracking',
      'Attendance analytics',
      'Tournament management',
      'Custom team branding',
      'Priority email support',
    ],
    ctaText: 'Get in touch',
    ctaHref: mailto('Growth tier inquiry', 'Growth ($349/mo)'),
    highlight: false,
  },
  {
    id: 'elite',
    name: 'Elite',
    price: '$899',
    period: '/mo',
    annualNote: '$749/mo billed annually',
    blurb: 'Established club, multi-site',
    teams: '30 teams',
    players: '750 players',
    features: [
      'Everything in Growth',
      'Multi-site organization',
      'Admin role hierarchy',
      'Advanced analytics',
      'Automated payment reminders',
      'Dedicated account manager',
      'Phone + email support',
    ],
    ctaText: 'Get in touch',
    ctaHref: mailto('Elite tier inquiry', 'Elite ($899/mo)'),
    highlight: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    annualNote: null,
    blurb: 'Leagues, white-label, SSO',
    teams: '50+ teams',
    players: 'Unlimited',
    features: [
      'Everything in Elite',
      'White-label option',
      'SSO & API access',
      'Custom integrations',
      'Onboarding specialist',
      'SLA guarantees',
    ],
    ctaText: 'Talk to sales',
    ctaHref: mailto('Enterprise inquiry'),
    highlight: false,
  },
]

const FAQS = [
  {
    q: 'Is the Free tier really free forever?',
    a: 'Yes. The Free tier is permanently free for solo coaches running a single team up to 25 players. No credit card required, no trial expiration. Paid tiers exist for teams and clubs that need more.',
  },
  {
    q: 'How does payment processing work?',
    a: 'All paid tiers include Scrimly Payments, powered by Stripe. We charge the standard Stripe rate (2.9% + $0.30) with no markup. Funds land directly in your club\'s bank account, usually within 2 business days. Installment plans add a small per-transaction fee to cover the extra processing.',
  },
  {
    q: 'What counts as a "team"?',
    a: 'A team is a roster of players coached as a single group, typically by age and competitive level (e.g. "U12 Boys Premier"). If you coach two age groups in the same season, that\'s two teams.',
  },
  {
    q: 'Can I switch tiers later?',
    a: 'Yes. Upgrade or downgrade anytime. Once self-serve subscriptions launch (Phase 3), you\'ll be able to do this from your billing settings. For now, just send a note to the Scrimly team and we\'ll handle it manually.',
  },
  {
    q: 'When do self-serve subscriptions launch?',
    a: 'Phase 3, after we have 10 founding clubs validating the tier structure. Until then, every paying customer is billed manually via Stripe invoice. The benefit: you get direct founder attention while we get real feedback on what\'s working.',
  },
  {
    q: 'What about the scrimmage-matching feature?',
    a: 'It\'s coming back. The original Scrimly was a scrimmage-finder, but the platform needed more team density before matching could be instant. We\'re building the rest of the club platform first, then bringing matching back as a feature inside it later this year.',
  },
]

// ----------------------------------------------------------------
// Tier card. Renders white on the pitch-dark page so cards pop and
// feature text stays readable. Highlighted (Club) tier gets a stronger
// lime ring and slight scale. Mirrors the marketing site's "scale"
// section pattern (white tier cards on a darker section).
// ----------------------------------------------------------------
function TierCard({ tier }) {
  return (
    <div
      className={[
        'relative flex flex-col rounded-2xl bg-white p-6 transition-all',
        tier.highlight
          ? 'ring-2 ring-[#b5f03d] shadow-[0_8px_32px_-4px_rgba(181,240,61,0.35)] sm:scale-[1.03]'
          : 'ring-1 ring-white/10 hover:ring-[#b5f03d]/40',
      ].join(' ')}
    >
      {tier.badge && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-[#b5f03d] text-[#0d1f14] px-3 py-1 text-[10px] font-bold uppercase shadow-md"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          <StarIcon className="h-3 w-3" />
          {tier.badge}
        </div>
      )}

      {/* Tier name */}
      <p
        className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2"
        style={{ fontFamily: HEAD_FONT }}
      >
        {tier.name}
      </p>

      {/* Price */}
      <div className="flex items-baseline gap-1 mb-1">
        <span
          className="text-4xl font-bold text-[#0d1f14]"
          style={{ fontFamily: HEAD_FONT }}
        >
          {tier.price}
        </span>
        {tier.period && (
          <span className="text-sm text-zinc-500 font-medium">{tier.period}</span>
        )}
      </div>
      {tier.annualNote ? (
        <p className="text-xs text-zinc-400 mb-3">{tier.annualNote}</p>
      ) : (
        <div className="mb-3" />
      )}

      {/* Blurb */}
      <p className="text-sm font-semibold text-[#0d1f14] mb-1">{tier.blurb}</p>

      {/* Teams + players */}
      <p className="text-xs text-zinc-500 mb-5">
        {tier.teams} · {tier.players}
      </p>

      {/* CTA */}
      {tier.ctaIsRoute ? (
        <Link
          to={tier.ctaHref}
          className={[
            'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold uppercase transition-all active:scale-[0.97] mb-5',
            tier.highlight
              ? 'bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]'
              : 'border border-zinc-200 bg-white text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10',
          ].join(' ')}
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          {tier.ctaText}
        </Link>
      ) : (
        <a
          href={tier.ctaHref}
          className={[
            'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold uppercase transition-all active:scale-[0.97] mb-5',
            tier.highlight
              ? 'bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]'
              : 'border border-zinc-200 bg-white text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10',
          ].join(' ')}
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          {tier.ctaText}
        </a>
      )}

      {/* Features */}
      <ul className="space-y-2.5 text-sm flex-grow">
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckIcon className="h-4 w-4 mt-0.5 text-[#5a8a1f] flex-shrink-0" />
            <span className="text-zinc-700">{f}</span>
          </li>
        ))}
        {tier.notIncluded?.map((f, i) => (
          <li key={`x-${i}`} className="flex items-start gap-2 opacity-50">
            <span className="h-4 w-4 mt-0.5 flex-shrink-0 text-zinc-400 text-center leading-tight">×</span>
            <span className="text-zinc-500 line-through decoration-zinc-300">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ----------------------------------------------------------------
// Main page (PITCH-DARK throughout, public-page convention)
// ----------------------------------------------------------------
export default function PricingPage() {
  // Reusable grid background for hero + section separators. Matches
  // the welcome modal and marketing site hero aesthetic.
  const gridBg = {
    backgroundImage:
      'linear-gradient(rgba(181,240,61,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.06) 1px, transparent 1px)',
    backgroundSize: '32px 32px',
  }

  return (
    <div className="min-h-screen bg-[#0d1f14] text-white">

      {/* Top bar with logo + sign-in link */}
      <header className="border-b border-white/10 bg-[#0d1f14]/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center" aria-label="Scrimly home">
            <span
              className="text-white leading-none"
              style={{
                fontFamily: HEAD_FONT,
                fontWeight: 900,
                fontSize: 26,
                letterSpacing: '-0.5px',
              }}
            >
              Scrim<span className="text-[#b5f03d]">ly</span>
            </span>
          </Link>
          <div className="flex items-center gap-4 sm:gap-6">
            <a
              href="https://getscrimly.com"
              className="text-xs font-bold uppercase tracking-wide text-white/60 hover:text-white hidden sm:inline-block"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              Home
            </a>
            <Link
              to="/login"
              className="text-xs font-bold uppercase tracking-wide text-[#b5f03d] hover:text-white"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              Sign In
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden px-4 sm:px-6 pt-12 sm:pt-20 pb-12 sm:pb-16" style={gridBg}>
        <div className="absolute top-0 right-0 -mr-32 -mt-32 w-96 h-96 rounded-full bg-[#b5f03d]/10 blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p
            className="text-xs font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-3"
            style={{ fontFamily: HEAD_FONT }}
          >
            Pricing
          </p>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-bold uppercase text-white mb-4 tracking-tight leading-none"
            style={{ fontFamily: HEAD_FONT }}
          >
            Same assistant coach.<br />
            <span className="text-[#b5f03d]">Any size team.</span>
          </h1>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Free forever for the solo coach with one team. Built to scale to clubs running hundreds. Pick the tier that fits where you are today.
          </p>
        </div>
      </section>

      {/* Tier grid */}
      <section className="px-4 sm:px-6 pb-16 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 pt-6">
          {TIERS.map((tier) => (
            <TierCard key={tier.id} tier={tier} />
          ))}
        </div>
        <p className="text-center text-xs text-white/50 mt-6">
          Annual plans save 17% (2 months free). Switch tiers anytime.
        </p>
      </section>

      {/* Founding Club spotlight */}
      <section className="px-4 sm:px-6 pb-16 max-w-5xl mx-auto">
        <div
          className="relative overflow-hidden rounded-3xl bg-[#0a1410] ring-1 ring-[#b5f03d]/20 p-8 sm:p-10 lg:p-12"
          style={gridBg}
        >
          <div className="absolute top-0 right-0 -mr-32 -mt-32 w-96 h-96 rounded-full bg-[#b5f03d]/15 blur-3xl pointer-events-none" />
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mb-4">
                <RocketLaunchIcon className="h-3.5 w-3.5 text-[#b5f03d]" />
                <span
                  className="text-[10px] font-bold tracking-[1.4px] uppercase text-[#b5f03d]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Founding Club Program
                </span>
              </div>
              <h2
                className="text-3xl sm:text-4xl font-bold uppercase leading-none mb-4 text-white"
                style={{ fontFamily: HEAD_FONT }}
              >
                $199/mo for the <span className="text-[#b5f03d]">Growth tier.</span>
              </h2>
              <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-6">
                First 5 clubs only. Locked at $199/mo — for life, as long as your account stays active. That's the $349 Growth tier at a permanent 43% discount, plus founder access and free migration.
              </p>
              <a
                href={mailto('Founding Club spot — interested')}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-6 py-3 text-sm font-bold uppercase shadow-lg transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                Claim a Founding Club spot
                <ChevronRightIcon className="h-4 w-4" />
              </a>
            </div>
            <ul className="space-y-3">
              {[
                'Growth-tier features at $199/mo, locked for life',
                'Stripe Payments at-cost forever (no platform markup)',
                'Free migration from Byga, TeamSnap, or LeagueApps',
                'Direct founder access (WhatsApp + weekly check-ins)',
                'Roadmap input, beta tester role on new features',
              ].map((perk) => (
                <li key={perk} className="flex items-start gap-3">
                  <CheckIcon className="h-5 w-5 text-[#b5f03d] flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-sm sm:text-base">{perk}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Payment processing details */}
      <section className="px-4 sm:px-6 pb-16 max-w-4xl mx-auto">
        <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-6 sm:p-8">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-2"
            style={{ fontFamily: HEAD_FONT }}
          >
            Payment Processing
          </p>
          <h2
            className="text-2xl font-bold uppercase text-white mb-3"
            style={{ fontFamily: HEAD_FONT }}
          >
            Stripe at-cost. No markup.
          </h2>
          <p className="text-sm text-white/70 leading-relaxed mb-5">
            All paid tiers include Scrimly Payments, powered by Stripe Connect. Funds land directly in your club's bank account. No platform fee on top.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-white p-4">
              <p className="text-xs font-bold uppercase text-[#5a8a1f] mb-1" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
                Standard
              </p>
              <p className="text-2xl font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>2.9% + $0.30</p>
              <p className="text-xs text-zinc-500 mt-1">Pay-in-full registrations</p>
            </div>
            <div className="rounded-xl bg-white p-4">
              <p className="text-xs font-bold uppercase text-[#5a8a1f] mb-1" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
                Plus
              </p>
              <p className="text-2xl font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>2.9% + $0.30 + $1</p>
              <p className="text-xs text-zinc-500 mt-1">Per installment for payment plans</p>
            </div>
          </div>
          <p className="text-xs text-white/50 mt-4 italic">
            Compared to Byga's effective 3.38% rate, families save about $13 on a $2,885 season.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 pb-16 max-w-3xl mx-auto">
        <h2
          className="text-2xl sm:text-3xl font-bold uppercase text-white mb-2 text-center"
          style={{ fontFamily: HEAD_FONT }}
        >
          Frequently Asked
        </h2>
        <p className="text-center text-white/60 mb-8 text-sm">
          Don't see your question? Just email us.
        </p>
        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <details
              key={i}
              className="group rounded-xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-4 hover:ring-[#b5f03d]/30 transition-colors"
            >
              <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
                <span className="font-semibold text-white text-sm sm:text-base">{faq.q}</span>
                <ChevronRightIcon className="h-4 w-4 text-white/40 group-open:rotate-90 transition-transform flex-shrink-0" />
              </summary>
              <p className="mt-3 text-sm text-white/70 leading-relaxed">{faq.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="px-4 sm:px-6 pb-20 max-w-3xl mx-auto text-center">
        <div className="rounded-2xl border-2 border-dashed border-[#b5f03d]/40 bg-[#b5f03d]/5 px-6 py-8 sm:px-8 sm:py-10">
          <p
            className="text-xs font-bold uppercase tracking-[1.4px] text-[#b5f03d] mb-2"
            style={{ fontFamily: HEAD_FONT }}
          >
            Private Beta
          </p>
          <h3
            className="text-2xl sm:text-3xl font-bold uppercase text-white mb-3 leading-tight"
            style={{ fontFamily: HEAD_FONT }}
          >
            Not open to the public yet.
          </h3>
          <p className="text-white/70 text-sm sm:text-base mb-5 max-w-xl mx-auto">
            We're polishing things with a small group of founding clubs. Self-serve subscriptions launch later this year. For now, contact us directly to discuss the right plan for your club.
          </p>
          <a
            href={mailto('Beta access request')}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-6 py-3 text-sm font-bold uppercase shadow-lg transition-all active:scale-[0.97]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            Request Beta Access
            <ChevronRightIcon className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0a1410]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>© {new Date().getFullYear()} Scrimly. Team management that feels like an assistant coach.</p>
          <div className="flex gap-4">
            <a href="https://getscrimly.com" className="hover:text-[#b5f03d]">Home</a>
            <Link to="/login" className="hover:text-[#b5f03d]">Sign in</Link>
            <Link to="/changelog" className="hover:text-[#b5f03d]">Changelog</Link>
            <a href={`mailto:${FOUNDER_EMAIL}`} className="hover:text-[#b5f03d]">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
